import React, { useEffect, useState } from 'react';
import { UnlockGuestControllerTypes, useGuestUnlockInfoController } from '../UnlockDialog/UnlockDialog.controller';
import { VerifyEmailForm } from './VerifyEmailForm';
import { FindInviteForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/FindInviteForm';
import { UnlockForm } from './UnlockForm/UnlockForm';
import FindInviteSuccessForm from './FindInviteSuccessForm/FindInviteSuccessForm';
import { useTranslation } from '@shared/core';
import { AskHostForm } from './AskHostForm/AskHostForm';
import { AskHostSuccessForm } from '@apps/guest/packages/layout-engine/components/UnlockDialog/AskHostSuccessForm';
import { DialogStyled } from '../DialogStyled/DialogStyled';
import { UnlockDialogContextValue, useUnlockDialogContext } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialogProvider';
import UnlockSuccess from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnclockSuccess/UnlockSuccess';
import { ButtonV2, ButtonV2Props } from '@withjoy/joykit';

interface ManageUnlockDialogFormsProps {
  unlockData: UnlockGuestControllerTypes;
  onDialogClose: () => void;
}

export interface GuestTypes {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  personIdTag?: string;
}

interface ButtonV2PropsExt extends ButtonV2Props {
  scheduleHasInviteOnlyItems: Maybe<boolean>;
  hasUnlocked: Maybe<boolean>;
  hasIdentity: Maybe<boolean>;
}

const ManageUnlockDialogForms: React.FC<ManageUnlockDialogFormsProps> = ({ unlockData, onDialogClose }) => {
  const { eventId, hasUnlocked, setShowBackButton, onBackButtonClick, shouldDisplayFindInvite } = unlockData;
  const [showUnlockSuccess, setShowUnlockSuccess] = useState<boolean>(false);
  const [showAskHost, setShowAskHost] = useState<boolean>(false);
  const [showAskHostSuccess, setShowAskHostSuccess] = useState<boolean>(false);
  const [guest, setGuest] = useState<GuestTypes>({ firstName: '', lastName: '', personIdTag: '' });
  const [guestsFound, setGuestsFound] = useState<number>(0);

  // Manage onClick Dialog Backbutton
  useEffect(() => {
    onBackButtonClick(() => {
      setShowAskHost(false);
      setGuestsFound(0);
    });
  }, [onBackButtonClick]);

  // Manage Dialog Backbutton conditions
  useEffect(() => {
    setShowBackButton(false);
    if (guestsFound > 1 && !guest.personIdTag) setShowBackButton(true);
    if (showAskHost) setShowBackButton(true);
  }, [guest.personIdTag, guestsFound, setShowBackButton, showAskHost]);

  // CB Only show UnlockSuccess if we don't have to display FindInvite
  const unlockSuccessHandle = () => {
    if (!shouldDisplayFindInvite) setShowUnlockSuccess(true);
  };

  // CB Only works if we don't have to display FindInvite
  const hideUnlockSuccessHandle = () => {
    if (!shouldDisplayFindInvite) {
      onDialogClose();
    }
  };

  // If we have a personIdTag it means that we have already unlocked the invitation
  if (guest.personIdTag) {
    return <FindInviteSuccessForm eventId={eventId} guest={guest} onDialogClose={onDialogClose} />;
  }

  // If we have more thant 1 guest found, it means that we have to verify the email.
  if (guestsFound > 1) {
    return <VerifyEmailForm eventId={eventId} guest={guest} setGuest={setGuest} />;
  }

  // If we have the event unlocked, show the invitation form or show success
  if (hasUnlocked) {
    if (shouldDisplayFindInvite) {
      return <FindInviteForm eventId={eventId} setGuestsFound={setGuestsFound} setGuest={setGuest} />;
    } else if (showUnlockSuccess) {
      return <UnlockSuccess onHide={hideUnlockSuccessHandle} />;
    }
  }

  // showAskHost Section
  if (showAskHost) {
    const onCloseHandler = () => {
      setShowAskHost(false);
      setShowAskHostSuccess(true);
    };
    return <AskHostForm eventId={eventId} onClose={onCloseHandler} />;
  }

  if (showAskHostSuccess) {
    return <AskHostSuccessForm onClose={onDialogClose} />;
  }

  // Show password form to unlock the event
  return <UnlockForm eventId={eventId} onUnlockSuccess={unlockSuccessHandle} onShowAskHost={() => setShowAskHost(true)} />;
};

const UnlockDialog: React.FC<UnlockDialogContextValue> & { TriggerButton: typeof TriggerButton } = ({ isOpen, onClose }) => {
  const unlockData = useGuestUnlockInfoController();
  const { showBackButton } = unlockData;

  return (
    <DialogStyled isOpen={isOpen} onClose={onClose}>
      <DialogStyled.Header>
        <DialogStyled.BackButton hidden={!showBackButton} />
        <DialogStyled.CloseButton onClick={onClose} />
      </DialogStyled.Header>
      <DialogStyled.Body>
        <ManageUnlockDialogForms unlockData={unlockData} onDialogClose={onClose} />
      </DialogStyled.Body>
      <DialogStyled.Footer />
    </DialogStyled>
  );
};

const TriggerButton: React.FC<ButtonV2PropsExt> = props => {
  const { onOpen } = useUnlockDialogContext();
  const { t2 } = useTranslation('guestUnlock');
  const { unlockButtonText, findInviteButtonText } = t2('dialogTriggerButton');
  const { hasUnlocked, hasIdentity, scheduleHasInviteOnlyItems } = props;
  const shouldDisplayFindInvite = scheduleHasInviteOnlyItems && !hasIdentity;

  if (hasUnlocked && !shouldDisplayFindInvite) return null;

  return (
    <ButtonV2 onClick={onOpen} {...props}>
      {!hasUnlocked ? unlockButtonText : findInviteButtonText}
    </ButtonV2>
  );
};

UnlockDialog.TriggerButton = TriggerButton;

export { UnlockDialog };
